import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { Category as CategoryIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import LedgerAccountField, { accountChange } from '../../finance/ledger/LedgerAccountField';
import { CategoryTreeConfig, TreeCategory } from './types';

const schema = yup.object({
  name: yup.string().trim().required('Name is required').max(100),
  description: yup.string().max(500),
});

/** Add or edit a category: its name, description and the ledger account it posts to. */
const CategoryDialog: React.FC<{
  open: boolean;
  editing: TreeCategory | null;
  config: CategoryTreeConfig;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, editing, config, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const initial = { name: editing?.name ?? '', description: editing?.description ?? '', glAccountCode: editing?.glAccountCode ?? '' };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initial,
    validationSchema: schema,
    onSubmit: async values => {
      const input = {
        name: values.name.trim(),
        description: values.description,
        ...accountChange(canWrite('finance_accounts'), values.glAccountCode, initial.glAccountCode),
      };
      try {
        if (editing) await config.service.updateCategory(editing._id, input);
        else await config.service.createCategory(input);
        enqueueSnackbar(editing ? 'Category updated' : 'Category created', { variant: 'success' });
        onSaved();
        onClose();
      } catch (error: any) {
        enqueueSnackbar(error.message || 'Could not save the category', { variant: 'error' });
      }
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon color={config.accent} />
          <Typography variant="h6" fontWeight={600}>{editing ? 'Edit Category' : 'Add Category'}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {editing ? 'Update the category details below' : `Create a new ${config.noun} category`}
        </Typography>
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <TextField
              fullWidth
              name="name"
              autoFocus
              label="Category Name"
              placeholder={config.examples.category}
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              fullWidth
              name="description"
              label="Description"
              placeholder="Brief description of this category"
              multiline
              rows={2}
              value={formik.values.description}
              onChange={formik.handleChange}
            />
            <LedgerAccountField
              use={config.accountUse}
              value={formik.values.glAccountCode}
              onChange={v => formik.setFieldValue('glAccountCode', v)}
              suggestedCode={editing?.suggestedAccountCode}
              help={config.accountHelp}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color={config.accent} disabled={formik.isSubmitting} sx={{ fontWeight: 600 }}>
            {editing ? 'Save Changes' : 'Create Category'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CategoryDialog;
